import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, Drawer, FormControlLabel, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostingService from '../../services/PostingService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { workFolderUrl } from '../work/WorkFolderSection';
import { CaptionFirst, CanvaLink, ReadOnlyText } from './PostTextSection';
import SetPostingDateDialog from './SetPostingDateDialog';
import { dayLabel, postingWordLabel } from './calendarUi';
import { platformLabel, useTaskPlatforms } from './taskFields';

const Language = ({ row, contentType, contentId, platforms, onTick }) => (
    <Box sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle2">{row.languageLabel}</Typography>
            <Chip
                size="small"
                variant="outlined"
                icon={row.word === 'POSTED' ? <CheckIcon /> : undefined}
                label={postingWordLabel(row.word)}
            />
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
            {[platformLabel(platforms, row.platform), row.date ? dayLabel(row.date, true) : 'No date'].filter(Boolean).join(' · ')}
        </Typography>
        {row.ready && (
            <>
                {row.caption && <CaptionFirst language={row.languageLabel} text={row.caption} />}
                {row.design && <ReadOnlyText label={`Text on design (${row.languageLabel})`} text={row.design} copy />}
                <Box>
                    <CanvaLink url={row.canvaUrl} />
                    <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                        href={workFolderUrl(contentId, row.languageKey, contentType)}
                        target="_blank"
                        rel="noopener"
                        sx={{ mt: 1, minHeight: 44 }}
                    >
                        Download files
                    </Button>
                </Box>
                <FormControlLabel
                    sx={{ display: 'flex', mt: 0.5, minHeight: 44 }}
                    control={<Checkbox checked={row.word === 'POSTED'} onChange={(e) => onTick(row, e.target.checked)} />}
                    label={`Posted${row.slot === 2 ? ` on ${platformLabel(platforms, row.platform)}` : ''}`}
                />
            </>
        )}
    </Box>
);

/**
 * What AV opens from Posting calendar (rev6 Part 11, 6j-6): that day's languages, in one column for
 * a phone. Ready languages show what's needed to post and a Posted tick. Others say Not ready yet.
 * date: the card's day; none shows every language, as from the Post bank.
 */
const PostingPanel = ({ row, onClose, onChanged }) => {
    useTrackUsage('PostingPanel', 'screen', 'posts', !!row);
    const platforms = useTaskPlatforms();
    const [view, setView] = useState(null);
    const [error, setError] = useState('');
    const [settingDate, setSettingDate] = useState(false);
    const taskId = row?.id;
    const date = row?.date;

    const load = useCallback(() => {
        if (!taskId) return;
        PostingService.panel(taskId, date)
            .then((v) => {
                setView(v);
                setError('');
            })
            .catch((e) => setError(taskErrorMessage(e)));
    }, [taskId, date]);

    useEffect(() => {
        setView(null);
        load();
    }, [load]);

    const tick = async (language, posted) => {
        try {
            await PostingService.setPosted(language.taskId, language.slot, posted);
            load();
            onChanged();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const firstSlot = view?.languages.filter((l) => l.slot === 1) || [];
    return (
        <Drawer anchor="right" open={!!row} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100%' } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                    {view?.title || row?.title || 'Posting'}
                </Typography>
                <IconButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {!view && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}
                {view?.languages.map((l, i) => (
                    <Box key={`${l.taskId}:${l.slot}`}>
                        {i > 0 && <Divider />}
                        <Language row={l} contentType={view.contentType} contentId={view.contentId} platforms={platforms} onTick={tick} />
                    </Box>
                ))}
                {view?.canSetPostingDate && (
                    <Button variant="outlined" sx={{ mt: 1, minHeight: 44 }} onClick={() => setSettingDate(true)}>
                        Set posting date
                    </Button>
                )}
            </Box>
            {settingDate && (
                <SetPostingDateDialog
                    post={{
                        postId: view.contentId,
                        strip: firstSlot.map((l) => ({
                            languageKey: l.languageKey,
                            languageLabel: l.languageLabel,
                            targetDate: l.date,
                            hasLink: l.word === 'POSTED',
                        })),
                    }}
                    onClose={() => setSettingDate(false)}
                    onSaved={() => {
                        setSettingDate(false);
                        load();
                        onChanged();
                    }}
                />
            )}
        </Drawer>
    );
};

export default PostingPanel;
